import { useState } from "react";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useStore,
  type Identifier,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  CircleSlash,
  Coffee,
  Handshake,
  Mail,
  Minus,
  Pause,
  Phone,
  Play,
  Presentation,
  Send,
  Sparkles,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import { RelativeDate, formatLocalizedDate } from "../misc/RelativeDate";
import type { ContentAsset } from "./ContentLibrary";
import { MARKETING_PATHS, contactTasksPath, lifecyclePath } from "./paths";
import { StepContentPanel } from "./StepContentPanel";
import { contactMergeValues } from "./stepContent";
import {
  activeEnrollment,
  describeEnding,
  endEnrollment,
  pastEnrollments,
  pauseEnrollment,
  pauseVerdict,
  planResume,
  progress,
  resumeEnrollment,
  suspension,
  validateEndReason,
} from "./enrollment";
import {
  clearCommitment,
  saveCommitment,
  validateCommitment,
} from "./commitment";
import {
  cadence,
  describeCadence,
  findStage,
  formatDay,
  headline,
  invalidateLifecycle,
  pauseState,
  saveLifecycle,
  stagePatch,
  stagesForTrack,
  tracksOf,
  validateCadence,
  validatePause,
} from "./lifecycleRules";
import {
  addStep,
  describeNurtureRun,
  moveStep,
  planStepInsertion,
  planStepRemoval,
  planEditLock,
  removalVerdict,
  removeStep,
  retimeStep,
  retimeVerdict,
  skipStep,
  skipVerdict,
  stepTaskState,
  taskTypeFor,
  type NurtureRun,
  type StepTask,
} from "./planRules";
import { todayLocal } from "./touchActions";
import { dateInputValue } from "./touchEdit";
import type {
  ContactHealth,
  ContactLifecycleRow,
  ContactWithLifecycle,
  Enrollment,
  EnrollmentStep,
  LifecycleStage,
  LifecycleTrack,
  Sequence,
} from "./types";

/**
 * ══════════════════════════════════════════════════════════════════════════
 * ONE CONTACT'S LIFECYCLE: THE PLAN, AND EVERYTHING THAT DECIDES IT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * WHY THIS SCREEN EXISTS, IN THE WORDS THAT ASKED FOR IT
 * ------------------------------------------------------
 * A contact was enrolled in Trade show follow-up. The panel on their record
 * said "0 of 5 steps done, and step 1 is next", gave a date, and offered a box
 * to end it. The person who did it concluded:
 *
 *     "basically nothing shows in the UI that I could do anything with… No
 *      emails or tasks seem to have appeared, so no idea it's even doing
 *      anything."
 *
 * The plan existed the whole time. Five steps, dated Sep 3, 7, 15, 25 and
 * Oct 9, one of them already turned into a task. NOTHING WAS BROKEN AND
 * NOTHING SAID SO. Twenty-one hours were spent believing a working system was
 * dead, because a progress counter is not a plan and "step 1 is next" is not
 * an answer to "when will anything happen".
 *
 * So this page is built around the two facts that were missing:
 *
 *   1. THE ROWS. One line per `enrollment_steps` row -- its position, its
 *      date, its status, the asset it sends and the task it produced -- which
 *      replaces "0 of 5" with the actual schedule.
 *   2. THE CLOCK. A step becomes a task only when a job runs, once a day,
 *      somewhere else. describeNurtureRun() says when it last ran. That one
 *      sentence is the whole difference between a system that has stopped and
 *      a system that is waiting.
 *
 * WHY THE TOUCH HISTORY IS FIRST
 * ------------------------------
 * Asked directly, and the answer was that it is "most important for deciding
 * if action is needed". Everything below it is a plan; the log is what
 * actually happened. A plan read without the history behind it is how somebody
 * sends a fourth follow-up to a person who replied last week.
 *
 * WHY THE CONTROLS MOVED OFF THE CONTACT RECORD
 * ---------------------------------------------
 * Track, stage, cadence and the pause were on the contact page, above the
 * notes feed. They are the inputs to the plan on this page -- the pause and
 * the commitment SUSPEND an enrollment, and the stage's cadence is what a
 * per-contact override overrides -- so they belong beside it rather than three
 * screens away from the thing they govern. What stays on the record is a
 * summary and the decision to START somebody, which is a judgment you make
 * while reading their record. See LifecyclePanel.tsx.
 */

/** Enough rows for any hand-built plan, read once. */
const ALL_STEPS = 1000;

/* ── the route components ──────────────────────────────────────────────── */

/**
 * `/contacts/:id/lifecycle`.
 *
 * A THIN WRAPPER, AND THE SPLIT IS DELIBERATE. Everything below takes the
 * contact id as a prop, so the screen can be mounted in a test without a
 * router entry and without pretending to be a route. The route is the only
 * thing that knows about `useParams`.
 */
export const ContactLifecycle = () => {
  const { id } = useParams();
  if (!id)
    return (
      <p className="text-sm text-muted-foreground">
        No contact in the address.
      </p>
    );
  return <LifecycleScreen contactId={id} />;
};

export const LifecycleScreen = ({ contactId }: { contactId: Identifier }) => {
  const today = todayLocal();

  const { data: contact } = useGetOne<ContactWithLifecycle>("contacts", {
    id: contactId,
  });

  /*
   * `contact_health`, NOT the contact record. The lifecycle columns are not
   * on it: the data provider rewrites getOne("contacts") to
   * `contacts_summary`, an upstream view that lists its columns explicitly,
   * and 065 removed the columns from `contacts` entirely. Reading anything
   * else here would be reading a second opinion. Same argument as
   * LifecyclePanel.tsx, which is where this query used to live.
   */
  const {
    data: healthRows,
    isPending: healthPending,
    error: healthError,
  } = useGetList<ContactHealth>("contact_health", {
    filter: { id: contactId },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });

  const { data: lifecycleRows } = useGetList<ContactLifecycleRow>(
    "contact_lifecycle",
    {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "contact_id", order: "ASC" },
    },
  );

  const { data: stages } = useGetList<LifecycleStage>("lifecycle_stages", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "sort", order: "ASC" },
  });

  const health = healthRows?.[0] ?? null;
  // NOT `?? {}`. A contact with no lifecycle row is the normal untriaged
  // state and has to stay distinguishable from a row full of nulls.
  const lifecycle = lifecycleRows?.[0] ?? null;
  const allStages = stages ?? [];

  const state = headline(health, today);
  const name = contact
    ? `${contact.first_name} ${contact.last_name}`.trim()
    : "this contact";

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 mb-2 text-muted-foreground"
        >
          <Link to={`/contacts/${contactId}/show`}>
            <ArrowLeft className="size-4" /> Back to {name}
          </Link>
        </Button>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-xl font-semibold">Lifecycle — {name}</h1>
          <span className="text-sm text-muted-foreground">
            {/* Silent while loading. "Not overdue" is a claim, and a claim
                must not be made from an answer we do not have yet. */}
            {healthPending ? null : <HeadlineText state={state} />}
          </span>
        </div>
      </div>

      {healthError ? (
        <p className="text-sm text-destructive">
          Could not read this contact&apos;s follow-up state, so this page is
          not a record of them being up to date — it is a record of not knowing.
        </p>
      ) : (
        <>
          <PlanSection
            contactId={contactId}
            contact={contact}
            health={health}
            today={today}
          />

          <ControlsSection
            contactId={contactId}
            health={health}
            lifecycle={lifecycle}
            allStages={allStages}
            state={state}
            today={today}
          />
        </>
      )}
    </div>
  );
};

/**
 * The cadence the track and stage just bought, stated plainly.
 *
 * `effective_stale_after` is the number contact_health resolved from
 * (stage, track) -- including a per-contact override -- so this prints the
 * cadence the nightly job will actually use rather than one this screen
 * derived for itself. Two derivations of one rule is how a screen comes to
 * disagree with the job it describes.
 */
const ConsequenceLine = ({
  health,
}: {
  health: ContactHealth | null | undefined;
}) => {
  if (!health) return null;
  if (health.is_terminal)
    return (
      <p className="text-xs text-muted-foreground">
        <strong>{health.track}</strong> ·{" "}
        <strong>{health.lifecycle_stage}</strong> closes the record — no
        cadence, and nothing will fall due.
      </p>
    );
  const days = health.effective_stale_after;
  return (
    <p className="text-xs text-muted-foreground">
      <strong>{health.track}</strong> ·{" "}
      <strong>{health.lifecycle_stage}</strong>
      {days != null ? (
        <>
          {" "}
          means chase every <strong>{days} days</strong>
        </>
      ) : (
        " has no cadence set"
      )}
      {health.due_at ? (
        <>
          . Next due {formatLocalizedDate(health.due_at)} (
          <RelativeDate date={health.due_at} />)
        </>
      ) : (
        ". No clock is running — this contact has never been contacted"
      )}
      .
    </p>
  );
};

const HeadlineText = ({ state }: { state: ReturnType<typeof headline> }) => {
  switch (state.kind) {
    case "terminal":
      return <>Closed — no cadence</>;
    case "paused":
      return <>Paused until {formatDay(state.until)}</>;
    case "overdue":
      return (
        <span className="text-destructive">
          Overdue since{" "}
          {state.dueAt ? <RelativeDate date={state.dueAt} /> : "— no clock"}
        </span>
      );
    case "due":
      return (
        <>Next due {state.dueAt ? <RelativeDate date={state.dueAt} /> : "—"}</>
      );
    default:
      // Not "fine". The read failed or this contact is not in the view.
      return <>Follow-up state unknown</>;
  }
};

/* ── the plan ──────────────────────────────────────────────────────────── */

const PlanSection = ({
  contactId,
  contact,
  health,
  today,
}: {
  contactId: Identifier;
  /** For the merge values. The step content is shown as this person will
   *  actually receive it, which needs their own record, not just their id. */
  contact: ContactWithLifecycle | null | undefined;
  health: ContactHealth | null;
  today: string;
}) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [endReason, setEndReason] = useState("");
  const [endError, setEndError] = useState<string | null>(null);

  const { data: enrollmentRows, error: enrollmentsError } =
    useGetList<Enrollment>("enrollments", {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "DESC" },
    });

  const { data: sequenceRows } = useGetList<Sequence>("sequences", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const active = activeEnrollment(enrollmentRows);
  const past = pastEnrollments(enrollmentRows);

  /*
   * THIS CONTACT'S OWN ROWS. `enabled` because there is nothing to filter by
   * without an active enrollment: `enrollment_id: undefined` is not a
   * narrower query, it is an unfiltered read of every step in the database.
   */
  const { data: planRows } = useGetList<EnrollmentStep>(
    "enrollment_steps",
    {
      filter: { enrollment_id: active?.id },
      pagination: { page: 1, perPage: ALL_STEPS },
      sort: { field: "step_no", order: "ASC" },
    },
    { enabled: active != null },
  );

  /*
   * The tasks this enrollment has produced. Read by `enrollment_id` rather
   * than one query per step, and matched back on `enrollment_step_id` --
   * which is the ONLY link between the two, and points one way. See
   * stepTaskState(): nothing in this app derives one from the other.
   */
  const { data: taskRows } = useGetList<StepTask & { id: number }>(
    "tasks",
    {
      filter: { enrollment_id: active?.id },
      pagination: { page: 1, perPage: ALL_STEPS },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: active != null },
  );

  const { data: assetRows } = useGetList<ContentAsset>("content_assets", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  /*
   * The nightly job's own record. THE SINGLE MOST IMPORTANT QUERY ON THIS
   * PAGE: it is the only thing that can say why a correctly-dated plan has
   * produced nothing yet.
   */
  const { data: runRows } = useGetList<NurtureRun & { id: number }>(
    "pipeline_runs",
    {
      filter: { job: "nurture-tick" },
      pagination: { page: 1, perPage: 5 },
      sort: { field: "started_at", order: "DESC" },
    },
  );

  const mergeValuesForContact = contactMergeValues(contact);
  const steps = planRows ?? [];
  const assets = assetRows ?? [];
  const tasksByStep = new Map<number, StepTask>();
  for (const task of taskRows ?? [])
    if (task.enrollment_step_id != null)
      tasksByStep.set(task.enrollment_step_id, task);

  const sequences = sequenceRows ?? [];
  const nameOf = (id: number | null) =>
    id == null
      ? "a hand-built plan"
      : // Never a blank. A row for a sequence this read did not return still
        // has to say WHICH one, and the id is the only honest answer left.
        (sequences.find((s) => s.id === id)?.name ?? `sequence #${id}`);

  const run = describeNurtureRun(runRows);
  const p = progress(steps);
  const held = suspension(health, today);

  const refresh = () => invalidateLifecycle(queryClient);

  /**
   * One wrapper for every plan write.
   *
   * INVALIDATION IS HERE AND NOWHERE ELSE, once per action rather than once
   * per request. These go through `useDataProvider()`, which bypasses
   * react-admin's mutation hooks and invalidates nothing on its own — the
   * failure the header of invalidateTouched() describes, where the toast
   * appears, the row is written and the screen keeps showing the old value.
   * And a browser test cannot catch its absence: Playwright's clicks fire
   * window focus events and react-query refetches on focus, so deleting this
   * line leaves the suite green. That is why it is one function with a name.
   */
  const write = async (what: string, action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      await refresh();
      notify(what, { type: "success" });
    } catch (e) {
      notify((e as Error).message ?? "That did not work", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    if (!active) return;
    const reason = validateEndReason(endReason);
    if (!reason.ok) {
      // Inline, not only a toast. The reason belongs beside the field that
      // caused it, and a toast is gone before anyone re-reads the box.
      setEndError(reason.message);
      return;
    }
    setEndError(null);
    await write("Enrollment ended.", async () => {
      await endEnrollment(dataProvider, active, reason.reason);
      setEndReason("");
    });
  };

  const pauseDrip = async () => {
    if (!active) return;
    await write("Drip paused. The follow-up clock is still running.", () =>
      pauseEnrollment(dataProvider, active),
    );
  };

  const resumeDrip = async () => {
    if (!active) return;
    await write("Drip resumed, and the remaining steps moved with it.", () =>
      resumeEnrollment(dataProvider, active, steps),
    );
  };

  /*
   * WHAT RESUMING WOULD DO, BEFORE ANYBODY PRESSES IT. Computed by the same
   * function that performs the write -- planResume() -- rather than a second
   * estimate of it, for planStepInsertion()'s reason: a preview derived
   * separately from the write is a preview that can be wrong about it.
   */
  const resumePreview =
    active?.status === "paused" && active.paused_at
      ? planResume(active, steps)
      : null;

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">The plan</h2>
        {active && (
          <span className="text-xs text-muted-foreground">
            {nameOf(active.sequence_id)}
          </span>
        )}
      </div>
      <Separator />

      {/*
        THE DRIP IS STOPPED AND THE ROW STILL READS AS THIS CONTACT'S LIVE
        ENROLLMENT, because enrollments_one_active covers `paused` -- the slot
        is kept on purpose, so nobody can be started on a second sequence
        while this one is suspended. Said here rather than left to a badge:
        "nothing is happening and nothing says why" is the sentence this whole
        page was built to make impossible.
      */}
      {active?.status === "paused" && (
        <div className="rounded-md border border-dashed px-3 py-2 text-sm space-y-2">
          <p>
            <strong>The drip is paused.</strong> No task is being created from
            this plan{" "}
            {active.paused_at ? (
              <>
                — stopped <RelativeDate date={active.paused_at} />
              </>
            ) : null}
            . This contact&apos;s own follow-up clock is still running, which is
            the point: you are chasing them by hand.
          </p>
          {/*
            A pending step keeps its date through the pause -- the generator
            simply never looks at it -- so resuming without moving them would
            make every step that came due meanwhile fire on the next run. The
            shift is what stops that, and it is stated before it happens.
          */}
          {resumePreview ? (
            <p className="text-xs text-muted-foreground">
              {resumePreview.moved.length === 0
                ? "Resuming moves nothing yet — no step has come due since the pause began."
                : `Resuming moves ${resumePreview.moved.length} pending step${
                    resumePreview.moved.length === 1 ? "" : "s"
                  } out by ${resumePreview.days} day${
                    resumePreview.days === 1 ? "" : "s"
                  }, keeping every gap between them.`}
              {resumePreview.frozen.length > 0
                ? ` ${resumePreview.frozen.length} step${
                    resumePreview.frozen.length === 1 ? " stays" : "s stay"
                  } where ${resumePreview.frozen.length === 1 ? "it is" : "they are"} — history does not move.`
                : ""}
            </p>
          ) : (
            <p className="text-xs text-destructive">
              This enrollment is paused but has no paused_at, so there is no way
              to tell how long for. Resuming cannot move the remaining steps
              safely — retime them by hand, then resume.
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={resumeDrip}
            disabled={busy || !resumePreview}
          >
            <Play className="size-4" /> Resume the drip
          </Button>
        </div>
      )}

      {enrollmentsError ? (
        <p className="text-sm text-destructive">
          Could not read this contact&apos;s enrollments, so this is not a
          record of them being in no sequence — it is a record of not knowing.
        </p>
      ) : !active ? (
        <p className="text-sm text-muted-foreground">
          Not in a sequence, so there is no plan. Enrolling is a decision you
          make while reading somebody&apos;s record, so the control is on{" "}
          <Link
            to={`/contacts/${contactId}/show`}
            className="underline underline-offset-2"
          >
            their contact page
          </Link>
          .
        </p>
      ) : planRows === undefined ? (
        <p className="text-sm text-muted-foreground">Reading the plan…</p>
      ) : steps.length === 0 ? (
        /*
          THE DEAD ROW. enroll_contact() (077) refuses a plan with no steps
          and is the only thing in this app that writes an enrollment, so
          nothing here can produce this — a hand-written insert can. It is
          worth saying because the failure is otherwise invisible: an
          enrollment with no pending step is marked `completed` by the next
          nightly run with ended_reason 'last_step', so the record claims this
          person was worked through a cadence they never received a word of,
          and until then it holds their one active slot.
        */
        <p className="text-sm text-destructive">
          This enrollment has no steps at all, so the nightly run will mark it
          completed as though the whole cadence had been worked — and until then
          it is holding this contact&apos;s one active slot. End it below and
          enroll again.
        </p>
      ) : (
        <>
          <ol className="divide-y rounded-md border">
            {steps.map((step, index) => (
              <StepRow
                key={step.id}
                step={step}
                contactId={contactId}
                values={mergeValuesForContact}
                onSaved={refresh}
                first={index === 0}
                last={index === steps.length - 1}
                asset={assets.find((a) => a.id === step.content_id) ?? null}
                task={tasksByStep.get(step.id) ?? null}
                steps={steps}
                busy={busy}
                onRetime={(day) =>
                  write(`Step ${step.step_no} moved.`, () =>
                    retimeStep(dataProvider, step, day),
                  )
                }
                onSkip={(reason) =>
                  write(`Step ${step.step_no} skipped.`, () =>
                    skipStep(dataProvider, step, reason),
                  )
                }
                onRemove={() =>
                  write(`Step ${step.step_no} removed.`, () =>
                    removeStep(dataProvider, steps, step.id),
                  )
                }
                onMove={(direction) =>
                  write("Steps swapped.", () =>
                    moveStep(dataProvider, steps, step.id, direction),
                  )
                }
              />
            ))}
          </ol>

          <NextRun
            run={run}
            next={p.next}
            held={held}
            dripPaused={active.status === "paused"}
          />

          <AddStep
            enrollmentId={active.id}
            steps={steps}
            assets={assets}
            busy={busy}
            onAdd={(spec) =>
              write("Step added.", () =>
                addStep(dataProvider, steps, active.id, spec),
              )
            }
          />

          <div className="flex flex-wrap items-end gap-2 pt-2">
            <div className="space-y-1.5 flex-1 min-w-48">
              <Label htmlFor="enrollment-end-reason">Why it is ending</Label>
              <Input
                id="enrollment-end-reason"
                placeholder="They bought from someone else…"
                value={endReason}
                onChange={(e) => {
                  setEndReason(e.target.value);
                  setEndError(null);
                }}
                disabled={busy}
              />
            </div>
            <Button variant="outline" size="sm" onClick={end} disabled={busy}>
              End enrollment
            </Button>
            {/*
              PAUSE IS NOT A SOFTER END, and the two sit together so the
              difference has to be read. Ending writes a reason and gives up
              the slot; pausing keeps both the slot and the plan, and the
              steps come back where the cadence put them. Offered only on an
              `active` row -- pauseVerdict() refuses the rest, and a control
              whose only outcome is a refusal is a control that lies.
            */}
            {pauseVerdict(active).ok && (
              <Button
                variant="outline"
                size="sm"
                onClick={pauseDrip}
                disabled={busy}
              >
                <Pause className="size-4" /> Pause the drip
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Pausing stops this sequence and nothing else — the follow-up clock
            keeps running and this contact stays on the accountability views. To
            silence everything, including their own reminder, pause the contact
            below instead.
          </p>
          {endError && <p className="text-xs text-destructive">{endError}</p>}
        </>
      )}

      {past.length > 0 && (
        <div className="pt-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            Before this
          </h3>
          {/*
            `completed` AND `ended` ARE NOT SYNONYMS, and this list is the only
            place the difference is visible: the generator writes `completed`
            with ended_reason 'last_step' when a cadence runs out of steps,
            while `ended` is somebody being taken off one. One person was
            worked all the way through and the other was not.
          */}
          <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
            {past.map((e) => (
              <li key={e.id}>
                <strong>{nameOf(e.sequence_id)}</strong> —{" "}
                {e.status === "completed" ? "completed" : "ended"}:{" "}
                {describeEnding(e)}
                {e.ended_at ? (
                  <>
                    {" ("}
                    <RelativeDate date={e.ended_at} />
                    {")"}
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

/**
 * Every row says what state it is in. None of them says nothing.
 *
 * The column holds `pending`, `generated` and `skipped`, and the first draft
 * printed that word raw -- on the argument that a friendly paraphrase
 * ("0 of 5 done") was the whole problem this screen exists to fix. That
 * argument is right about SUMMARIES and wrong about a single row: `pending`
 * in a low-contrast outline badge read as an empty cell, so the plan appeared
 * to have one annotated step and four blank ones.
 *
 * So the label is plain English and the database's own word is in the title
 * attribute, reachable by anyone who goes on to read the table. "Task created"
 * rather than "generated" also states the thing that actually happened, which
 * is what a person deciding whether to act needs.
 */
const STATUS_LABEL: Record<string, string> = {
  pending: "Scheduled",
  generated: "Task created",
  skipped: "Skipped",
};

const STATUS_STYLE = {
  pending: "secondary",
  generated: "default",
  skipped: "outline",
} as const satisfies Record<string, "outline" | "default" | "secondary">;

/**
 * The kind of contact a step asks for, as an icon and a word.
 *
 * Keyed on the configured task types, which are DATA -- Settings edits them --
 * so an unknown key must render rather than crash. It falls back to a neutral
 * badge carrying whatever word it was given, the same posture the task_type
 * guard takes in 075: refuse to invent a meaning, but never hide the value.
 */
const TYPE_STYLE: Record<string, { icon: typeof Mail; className: string }> = {
  email: {
    icon: Mail,
    className: "border-sky-500/40 text-sky-700 dark:text-sky-300",
  },
  call: {
    icon: Phone,
    className: "border-emerald-500/40 text-emerald-700 dark:text-emerald-300",
  },
  demo: {
    icon: Presentation,
    className: "border-violet-500/40 text-violet-700 dark:text-violet-300",
  },
  meeting: {
    icon: Handshake,
    className: "border-amber-500/40 text-amber-700 dark:text-amber-300",
  },
  lunch: {
    icon: Coffee,
    className: "border-amber-500/40 text-amber-700 dark:text-amber-300",
  },
  "follow-up": {
    icon: CalendarClock,
    className: "border-slate-500/40 text-slate-700 dark:text-slate-300",
  },
  "thank-you": {
    icon: Sparkles,
    className: "border-pink-500/40 text-pink-700 dark:text-pink-300",
  },
  ship: {
    icon: Send,
    className: "border-teal-500/40 text-teal-700 dark:text-teal-300",
  },
  none: {
    icon: CircleSlash,
    className: "border-muted-foreground/30 text-muted-foreground",
  },
};

const TypeBadge = ({
  taskType,
  source,
}: {
  taskType: string | null;
  source: string;
}) => {
  if (!taskType)
    return (
      <Badge
        variant="outline"
        className="gap-1 text-muted-foreground"
        title="No task type: this step has no content to derive one from, and none was set on it"
      >
        <Minus className="size-3" />
        no type
      </Badge>
    );
  const style = TYPE_STYLE[taskType];
  const Icon = style?.icon ?? Minus;
  return (
    <Badge
      variant="outline"
      className={`gap-1 ${style?.className ?? "text-muted-foreground"}`}
      title={source}
    >
      <Icon className="size-3" />
      {taskType}
    </Badge>
  );
};

/**
 * One row of the plan.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A CONTROL THAT CANNOT BE USED IS SHOWN, DISABLED, WITH THE REASON.
 * ══════════════════════════════════════════════════════════════════════════
 * Hiding it would teach nobody why; disabling it silently is worse, because a
 * greyed button with no explanation reads as the app being broken rather than
 * as the database protecting something. Every refusal below is a sentence
 * from planRules.ts, rendered where the button is.
 *
 * THE STEP'S STATUS AND THE TASK'S STATUS ARE PRINTED AS TWO FACTS. They are
 * joined at birth and independent afterwards: `tasks.enrollment_step_id` is
 * the only link and nothing writes back, so ticking the task leaves the step
 * `generated` for ever. Where they contradict each other, the row says so.
 */
/**
 * What to call the task in a sentence.
 *
 * NEVER A BLANK PAIR OF QUOTES. `tasks.text` is nullable and the generator
 * is not its only writer, so a row with no text still has to name itself --
 * the id is the only honest answer left, and it is also the thing somebody
 * would search for.
 */
const taskLabel = (task: StepTask): string =>
  task.text?.trim() || `Task ${task.id}`;

const StepRow = ({
  step,
  values,
  onSaved,
  first,
  last,
  contactId,
  asset,
  task,
  steps,
  busy,
  onRetime,
  onSkip,
  onRemove,
  onMove,
}: {
  step: EnrollmentStep;
  /** For the link to the task this step wrote; tasks have no route of their own. */
  contactId: Identifier;
  /** Merge values, so the step is shown as this contact will receive it. */
  values: Record<string, unknown>;
  /** Re-reads the plan after the content panel writes an override. */
  onSaved: () => void;
  /** Whether there is a neighbor to swap with. Not a position claim. */
  first: boolean;
  last: boolean;
  asset: ContentAsset | null;
  task: StepTask | null;
  steps: readonly EnrollmentStep[];
  busy: boolean;
  onRetime: (day: string) => void;
  onSkip: (reason: string) => void;
  onRemove: () => void;
  onMove: (direction: "up" | "down") => void;
}) => {
  const [open, setOpen] = useState<null | "retime" | "skip" | "remove">(null);
  /*
    THE CONTENT IS COLLAPSED BY DEFAULT. A plan is read as a list of five
    things in an order; five call scripts opened at once is not that list any
    more. It opens on the title because the title is the thing you are asking
    a question about.
  */
  const [showContent, setShowContent] = useState(false);
  const [day, setDay] = useState(dateInputValue(step.due_at));
  const [reason, setReason] = useState("");

  const canRetime = retimeVerdict(step);
  const canSkip = skipVerdict(step);
  const canRemove = removalVerdict(step);
  /*
    The three verdicts above stay, because each button is disabled by its own
    rule and each write is guarded by its own. This is only what gets PRINTED.
  */
  const lock = planEditLock(step);
  const taskState = stepTaskState(step, task);
  const derived = taskTypeFor(asset);
  const removal = planStepRemoval(steps, step.id);

  return (
    <li className="p-3 space-y-2">
      <div className="flex flex-wrap items-start gap-3">
        {/*
          `step_no` ITSELF, not a 1-based index over the rows.
          On this table the column is ORDER, NOT POSITION, and it is not
          required to be contiguous -- 078 does no arithmetic on it, so 1, 3,
          7 is a legal plan. Printing an invented ordinal beside it would give
          the screen a second numbering that disagrees with the database the
          moment anybody looks at a row directly.
        */}
        <span className="w-6 shrink-0 text-sm text-muted-foreground tabular-nums">
          {step.step_no}
        </span>

        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm leading-snug">
            <button
              type="button"
              onClick={() => setShowContent((v) => !v)}
              aria-expanded={showContent}
              aria-controls={`step-content-${step.id}`}
              className="inline-flex items-center gap-1 text-left font-bold hover:underline underline-offset-2"
            >
              {showContent ? (
                <ChevronDown className="size-3.5 shrink-0" />
              ) : (
                <ChevronRight className="size-3.5 shrink-0" />
              )}
              {asset ? asset.name : "No content — a bare step"}
            </button>
            {/*
              The step's task_type is a NULLABLE OVERRIDE since 075. Printing
              the derived value as though it were stored would hide which of
              the two a reader is looking at.
            */}{" "}
            <TypeBadge
              taskType={
                step.task_type ??
                (asset && derived.kind === "derived" ? derived.taskType : null)
              }
              source={
                step.task_type
                  ? "Set on this step, overriding the asset's kind"
                  : "Derived from the content asset's kind — not stored on the step"
              }
            />
            {step.source === "added" && (
              <span className="text-xs text-muted-foreground">
                {" · added by hand"}
              </span>
            )}
            {step.edited && (
              <span className="text-xs text-muted-foreground">
                {" · edited"}
              </span>
            )}
          </p>

          <p className="text-xs text-muted-foreground">
            Due {formatLocalizedDate(step.due_at)} (
            <RelativeDate date={step.due_at} />) · {step.offset_days} day
            {step.offset_days === 1 ? "" : "s"} after the step before it
          </p>

          {step.status === "skipped" && step.skipped_reason && (
            <p className="text-xs">Skipped — “{step.skipped_reason}”</p>
          )}

          <p
            className={
              taskState.kind === "disagree"
                ? "text-xs text-destructive"
                : "text-xs text-muted-foreground"
            }
          >
            {task ? (
              <>
                Task: {task.done_date ? "closed" : "open"}
                {task.due_date ? (
                  <>
                    , due <RelativeDate date={task.due_date} />
                  </>
                ) : null}
                {". "}
              </>
            ) : null}
            {taskState.sentence}
          </p>

          {/*
            WHAT THIS STEP ACTUALLY CAUSED, in the task's own words, and a
            way to go and look at it.

            IT NOW LINKS FOR A CLOSED TASK TOO, WHICH IT COULD NOT BEFORE.
            This printed a dead-end sentence for closed tasks -- "Closed tasks
            drop off the contact page, so there is nothing left to open" --
            and that sentence was true: every surface rendering a task kept
            only `!isDone || isRecentlyDone`, so five minutes after it was
            ticked the row existed in the database and nowhere in the app
            (atomic#15). The step is the record that work was ASKED for; the
            closed task is the only record that it was DONE, and this screen
            could describe it without ever showing it.

            /contacts/:id/tasks is that destination. So the two branches below
            differ only in WHERE they send you -- the contact page for open
            work, the task history for closed -- and neither is a promise the
            destination cannot keep.

            STILL NO PER-TASK ROUTE, deliberately. `#/tasks/:id` would be a
            third surface to build and keep honest for a link that lands two
            rows further down the same page this one opens; the task history
            is scoped, back-linked and already needed for the owner's own
            request. Revisit it only if something ever needs to address ONE
            task -- an email, a notification -- which nothing does today.
          */}
          {task && (
            <p className="text-xs">
              <span className="text-muted-foreground">It wrote </span>
              <Link
                to={
                  task.done_date
                    ? contactTasksPath(contactId)
                    : `/contacts/${contactId}/show`
                }
                className="underline underline-offset-2"
              >
                “{taskLabel(task)}”
              </Link>
              <span className="text-muted-foreground">
                {" "}
                {task.done_date
                  ? "— closed; in this contact's task history."
                  : "— open on the contact page."}
              </span>
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge
            variant={STATUS_STYLE[step.status] ?? "outline"}
            title={`enrollment_steps.status = '${step.status}'`}
          >
            {STATUS_LABEL[step.status] ?? step.status}
          </Badge>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              aria-label={`Move step ${step.step_no} earlier`}
              title={
                first
                  ? "Already the first step"
                  : "Move earlier — swaps this step's date with the one above"
              }
              disabled={busy || first}
              onClick={() => onMove("up")}
            >
              <ArrowUp className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              aria-label={`Move step ${step.step_no} later`}
              title={
                last
                  ? "Already the last step"
                  : "Move later — swaps this step's date with the one below"
              }
              disabled={busy || last}
              onClick={() => onMove("down")}
            >
              <ArrowDown className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-9">
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          disabled={busy || !canRetime.ok}
          onClick={() => {
            // Re-seeded on every open. The state would otherwise keep the
            // date this row had when it first mounted, so retiming a step and
            // opening the box again would offer the OLD date back as though
            // nothing had changed.
            setDay(dateInputValue(step.due_at));
            setOpen(open === "retime" ? null : "retime");
          }}
        >
          Retime
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          disabled={busy || !canSkip.ok}
          onClick={() => setOpen(open === "skip" ? null : "skip")}
        >
          Skip
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          disabled={busy || !canRemove.ok}
          onClick={() => setOpen(open === "remove" ? null : "remove")}
        >
          Remove
        </Button>
      </div>

      {/*
        THE REASON, ALWAYS -- so a disabled button is never a mystery, and
        never on hover, because a tooltip is invisible to somebody scanning
        the page for why nothing can be done to a step.

        ONE LINE WHEN ALL THREE REFUSE. They always do today: retime, skip
        and remove all decide on `step.status` alone and all three allow only
        `pending`, so three bullets restated one fact and a reader had to
        compare them to find that out. planEditLock() merges them only when
        it has checked that all three actually refuse; if one ever diverges
        the itemized list comes back on its own.
      */}
      {lock.locked &&
        (lock.scope === "all" ? (
          <p className="pl-9 text-xs text-muted-foreground">{lock.reason}</p>
        ) : (
          <ul className="pl-9 space-y-0.5 text-xs text-muted-foreground">
            {lock.refusals.map((r) => (
              <li key={r.action}>
                Cannot {r.action}: {r.reason}
              </li>
            ))}
          </ul>
        ))}

      {showContent && (
        <div id={`step-content-${step.id}`} className="pl-9">
          <StepContentPanel
            step={step}
            asset={asset}
            values={values}
            onSaved={onSaved}
          />
        </div>
      )}

      {open === "retime" && canRetime.ok && (
        <div className="pl-9 flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor={`retime-${step.id}`}>New date</Label>
            <Input
              id={`retime-${step.id}`}
              type="date"
              className="w-44"
              value={day}
              onChange={(e) => setDay(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              onRetime(day);
              setOpen(null);
            }}
          >
            Move it
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            A date in the past is allowed and means &ldquo;send it on the next
            run&rdquo; — the job&apos;s test is <code>due_at &lt;= now()</code>.
            Only this step moves; the ones after it stay where they are.
          </p>
        </div>
      )}

      {open === "skip" && canSkip.ok && (
        <div className="pl-9 flex flex-wrap items-end gap-2">
          <div className="space-y-1.5 flex-1 min-w-48">
            <Label htmlFor={`skip-${step.id}`}>Why it is being skipped</Label>
            <Input
              id={`skip-${step.id}`}
              placeholder="They already replied to this…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              onSkip(reason);
              setOpen(null);
            }}
          >
            Skip it
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            The reason is required —{" "}
            <code>enrollment_steps_skip_has_reason</code> refuses a skip without
            one, and a step that vanishes from a plan with no reason is
            indistinguishable from one nobody meant to drop. A skipped step
            stays on the plan; it is not deleted.
          </p>
        </div>
      )}

      {open === "remove" && canRemove.ok && removal && (
        <div className="pl-9 space-y-2 rounded-md border border-dashed p-3">
          <p className="text-xs">
            {removal.absorbedBy ? (
              <>
                <strong>No date moves.</strong> The {removal.absorbedDays} day
                {removal.absorbedDays === 1 ? "" : "s"} this step was waiting
                move onto step {removal.absorbedBy.step_no}, so everything after
                it stays exactly where it is. Dropping the wait instead would
                pull the rest of the plan {removal.absorbedDays} day
                {removal.absorbedDays === 1 ? "" : "s"} earlier.
              </>
            ) : (
              <>
                <strong>Nothing follows this step</strong>, so nothing moves.
              </>
            )}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={() => {
                onRemove();
                setOpen(null);
              }}
            >
              Remove step
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
              Keep it
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

/**
 * When anything will actually happen.
 *
 * THE SENTENCE THIS PAGE WAS BUILT AROUND. Every date above is correct and
 * none of them does anything on its own: a step becomes a task when a job
 * runs, once a day, elsewhere. Saying only "step 2 is due Sep 7" is what
 * produced twenty-one hours of believing the feature was dead.
 */
const NextRun = ({
  run,
  next,
  held,
  dripPaused,
}: {
  run: ReturnType<typeof describeNurtureRun>;
  next: EnrollmentStep | null;
  held: ReturnType<typeof suspension>;
  /** `enrollments.status = 'paused'`. A THIRD suppressor, and not the same
   *  as either of the two `held` reports — this one leaves the contact's own
   *  follow-up clock alone. */
  dripPaused: boolean;
}) => (
  <div
    className={`rounded-md border px-3 py-2 text-sm ${run.overdue || !run.healthy ? "border-destructive" : "border-dashed"}`}
  >
    <p>
      {next ? (
        <>
          Step {next.step_no} is due {formatLocalizedDate(next.due_at)} (
          <RelativeDate date={next.due_at} />
          ).{" "}
        </>
      ) : dripPaused ? (
        // NOT "the next run marks this enrollment completed". The generator
        // reads `e.status = 'active'`, so a paused enrollment is not
        // completed either -- saying it would be would be a threat the job
        // does not carry out.
        <>Nothing is pending. </>
      ) : (
        <>
          Nothing is pending, so the next run marks this enrollment
          completed.{" "}
        </>
      )}
      <span className={run.overdue || !run.healthy ? "text-destructive" : ""}>
        {run.sentence}
      </span>
    </p>

    {/*
      A live pause or a future commitment makes advance_enrollments() skip the
      enrollment entirely: no task, and the due step LEFT PENDING with its
      due_at in the past, so it fires the instant the hold lifts rather than
      being skipped. The row still says `active` throughout, so a screen that
      printed the status column would explain nothing at all about why no
      tasks are appearing.
    */}
    {/*
      THE DRIP PAUSE IS CHECKED SEPARATELY FROM `held`, because it is a
      different column on a different table meaning a different thing.
      suspension() reads the CONTACT's paused_until and commitment_at; this
      reads the ENROLLMENT's status. All three suppress the generator; only
      the contact pause also silences their follow-up reminder.
    */}
    {dripPaused && (
      <p className="mt-1 text-xs">
        <strong>And the drip is paused,</strong> so that date will pass without
        a task. The step is not skipped and keeps its place — resuming moves it,
        and everything after it, out by however long the pause ran.
      </p>
    )}

    {held.suspended && (
      <p className="mt-1 text-xs">
        <strong>And it is suspended anyway.</strong>
        {held.pausedUntil ? (
          <>
            {" "}
            Held by the pause until{" "}
            <strong>{formatDay(held.pausedUntil)}</strong>
            {held.pauseReason ? ` — ${held.pauseReason}` : ""}.
          </>
        ) : null}
        {held.commitmentAt ? (
          <>
            {" "}
            Held until the meeting <RelativeDate date={held.commitmentAt} />
            {held.commitmentNote ? ` — ${held.commitmentNote}` : ""}.
          </>
        ) : null}{" "}
        No task is being created while that holds, and the step is not skipped:
        it falls due the moment the hold lifts.
      </p>
    )}
  </div>
);

/**
 * Add a step from the content library.
 *
 * THE PREVIEW IS NOT DECORATION. `offset_days` counts from the PREVIOUS step,
 * so a step inserted mid-plan with a three-day wait pushes every pending step
 * after it three days out. That is the correct behavior — it preserves every
 * gap the plan already described — and it is exactly the kind of change that
 * must not happen silently. The list below is planStepInsertion()'s own
 * answer, computed from the same function that performs the write.
 *
 * THE TASK TYPE IS ASKED FOR WHEN IT CANNOT BE DERIVED.
 * `enrollment_steps_task_type_is_configured` fires BEFORE INSERT and derives
 * the effective type from the asset's kind; `linkedin`, `note` and `other`
 * have no counterpart in `configuration.config->'taskTypes'` and raise. 066
 * exists because a plausible-looking task type was once assumed rather than
 * checked, so this asks instead of guessing.
 */
const AddStep = ({
  enrollmentId,
  steps,
  assets,
  busy,
  onAdd,
}: {
  enrollmentId: number;
  steps: readonly EnrollmentStep[];
  assets: readonly ContentAsset[];
  busy: boolean;
  onAdd: (spec: {
    afterId: number | null;
    waitDays: number;
    content_id: number | null;
    task_type: string | null;
  }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const last = [...steps].sort((a, b) => a.step_no - b.step_no).at(-1);
  const [afterId, setAfterId] = useState<string>(String(last?.id ?? "front"));
  const [wait, setWait] = useState("7");
  const [contentId, setContentId] = useState<string>("none");
  const [taskType, setTaskType] = useState<string>("derive");

  if (!open)
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={busy}
      >
        Add a step
      </Button>
    );

  const asset =
    contentId === "none"
      ? null
      : (assets.find((a) => String(a.id) === contentId) ?? null);
  const choice = taskTypeFor(asset);
  const waitDays = Number(wait);
  const validWait = Number.isInteger(waitDays) && waitDays >= 0;

  // Retired assets are not offered. is_active is the only retirement path
  // this schema has -- nothing is ever deleted, because enrollment_steps
  // references it -- so offering one would defeat the single control a
  // library author has.
  const offerable = assets.filter((a) => a.is_active);

  const spec = {
    afterId: afterId === "front" ? null : Number(afterId),
    waitDays: validWait ? waitDays : 0,
    content_id: asset ? asset.id : null,
    // NULL means "ask the asset" -- 075's nullable override. It is the right
    // value for a derivable kind and the only value available for one that is
    // not, which is why `mustChoose` below blocks the button rather than
    // inventing something plausible here.
    task_type: taskType === "derive" ? null : taskType,
  };
  const preview = validWait
    ? planStepInsertion(steps, enrollmentId, spec)
    : null;

  const mustChoose = choice.kind === "must-choose" && taskType === "derive";

  return (
    <div className="space-y-3 rounded-md border p-3">
      <h3 className="text-sm font-semibold">Add a step</h3>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="add-after">After</Label>
          <Select value={afterId} onValueChange={setAfterId}>
            <SelectTrigger id="add-after" className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="front">Before everything</SelectItem>
              {[...steps]
                .sort((a, b) => a.step_no - b.step_no)
                .map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    Step {s.step_no} ({formatLocalizedDate(s.due_at)})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="add-wait">Days after that</Label>
          <Input
            id="add-wait"
            type="number"
            min={0}
            className="w-28"
            value={wait}
            onChange={(e) => setWait(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="add-content">Content</Label>
          <Select value={contentId} onValueChange={setContentId}>
            <SelectTrigger id="add-content" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* A bare "call them, no script" step is legitimate --
                  content_id is nullable on purpose. */}
              <SelectItem value="none">No content — a bare step</SelectItem>
              {offerable.map((a) => (
                <SelectItem key={a.id} value={String(a.id)}>
                  {a.name} · {a.kind}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="add-type">Task type</Label>
          <Select value={taskType} onValueChange={setTaskType}>
            <SelectTrigger id="add-type" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="derive">
                {choice.kind === "derived"
                  ? `From the asset (${choice.taskType})`
                  : "Not set"}
              </SelectItem>
              {/* The configured slugs, lower-case, NOT the labels. 67 tasks
                  once held the label "Follow-up" instead of the value
                  "follow-up" and rendered correctly while being wrong. */}
              {["email", "call", "follow-up", "meeting", "demo", "ship"].map(
                (t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{choice.note}</p>

      {!validWait && (
        <p className="text-xs text-destructive">
          The wait must be a whole number of days, zero or more —
          enrollment_steps_offset_check.
        </p>
      )}

      {mustChoose && (
        <p className="text-xs text-destructive">
          Pick a task type. The database refuses this step without one, and it
          would refuse it at the moment of saving rather than now.
        </p>
      )}

      {preview && (
        <div className="rounded-md border border-dashed p-3 text-xs space-y-1">
          <p>
            The new step lands on{" "}
            <strong>{formatLocalizedDate(preview.create.due_at)}</strong>.
          </p>
          {preview.moved.length > 0 ? (
            <>
              <p className="font-medium">
                {preview.moved.length} step
                {preview.moved.length === 1 ? "" : "s"} move, because every
                later step waits from the one before it:
              </p>
              <ul className="space-y-0.5">
                {preview.moved.map(({ step, from, to }) => (
                  <li key={step.id}>
                    Step {step.step_no}: {formatLocalizedDate(from)} →{" "}
                    <strong>{formatLocalizedDate(to)}</strong>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>Nothing else moves.</p>
          )}
          {preview.frozen.length > 0 && (
            <ul className="space-y-0.5 text-muted-foreground">
              {/* Listed rather than omitted: silence about an exclusion is
                  the same defect as silence about a lead. */}
              {preview.frozen.map(({ step, reason }) => (
                <li key={step.id}>
                  Step {step.step_no} does not move — it {reason}.
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !validWait || mustChoose}
          onClick={() => {
            onAdd(spec);
            setOpen(false);
          }}
        >
          Add it
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

/* ── track, stage, cadence and the pause ───────────────────────────────── */

/**
 * The four controls that decide the plan above, moved off the contact record.
 *
 * They are the same controls LifecyclePanel.tsx used to carry, with the same
 * reasoning intact — a pause is dated and a commitment is not the same thing;
 * the stage and the track are ONE key; the cadence field shows an override
 * over an inherited placeholder. What changed is where they live, and only
 * because the thing they govern now has a screen.
 */
const ControlsSection = ({
  contactId,
  health,
  lifecycle,
  allStages,
  state,
  today,
}: {
  contactId: Identifier;
  health: ContactHealth | null;
  lifecycle: ContactLifecycleRow | null;
  allStages: LifecycleStage[];
  state: ReturnType<typeof headline>;
  today: string;
}) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const notify = useNotify();

  /*
   * The track being LOOKED AT, which is not yet the track on record.
   * Switching it alone writes nothing, on purpose: ('demo','partner') is not
   * a row in lifecycle_stages, so a track written without a matching stage is
   * either a foreign-key error or -- worse, if the stage exists in both
   * tracks -- a silent cadence change from 30 days to 7. The write happens
   * when a STAGE is chosen, and it carries both columns.
   */
  const [draftTrack, setDraftTrack] = useState<LifecycleTrack | null>(null);
  const track = draftTrack ?? health?.track ?? null;

  const [cadenceDraft, setCadenceDraft] = useState<string | null>(null);
  const [cadenceError, setCadenceError] = useState<string | null>(null);
  const [pauseUntil, setPauseUntil] = useState("");
  const [pauseReason, setPauseReason] = useState("");
  const [pauseError, setPauseError] = useState<string | null>(null);
  const [commitDay, setCommitDay] = useState("");
  const [commitNote, setCommitNote] = useState("");
  const [commitError, setCommitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const write = async (patch: Parameters<typeof saveLifecycle>[2]) => {
    setBusy(true);
    try {
      await saveLifecycle(dataProvider, contactId, patch);
      await invalidateLifecycle(queryClient);
      return true;
    } catch (e) {
      notify((e as Error).message ?? "Could not save the lifecycle", {
        type: "error",
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const chooseStage = async (stage: string) => {
    if (!track) return;
    let patch;
    try {
      // Throws on a pair that is not in the table. The picker cannot offer
      // one, but a picker is a suggestion and this is what reaches the
      // database.
      patch = stagePatch(stage, track, allStages);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
      return;
    }
    if (await write(patch)) {
      setDraftTrack(null);
      notify(`Stage set to ${track} · ${stage}.`, { type: "success" });
    }
  };

  const applyCadence = async (raw: string) => {
    const check = validateCadence(raw);
    if (!check.ok) {
      setCadenceError(check.reason);
      return;
    }
    setCadenceError(null);
    // null, not 0 and not "leave it alone": clearing the field is how a
    // contact goes back to inheriting its stage's cadence, and NULL is what
    // makes contact_health's coalesce fall through to the stage again.
    if (await write({ stale_after_days: check.days })) {
      setCadenceDraft(null);
      notify(
        check.days == null
          ? "Back to the stage's own cadence."
          : `Chasing this contact every ${check.days} days.`,
        { type: "success" },
      );
    }
  };

  const pause = async () => {
    const check = validatePause(pauseUntil, today);
    if (!check.ok) {
      setPauseError(check.reason);
      return;
    }
    setPauseError(null);
    if (
      await write({
        paused_until: check.until,
        paused_reason: pauseReason.trim() || null,
      })
    ) {
      setPauseUntil("");
      setPauseReason("");
      notify(`Quiet until ${formatDay(check.until)}.`, { type: "success" });
    }
  };

  const resume = async () => {
    if (await write({ paused_until: null, paused_reason: null }))
      notify("Pause lifted — this contact is back on the clock.", {
        type: "success",
      });
  };

  /*
   * THE COMMITMENT GOES THROUGH ITS OWN MODULE, not through write() above.
   * saveCommitment() owns the day-to-instant conversion and the refusal of a
   * date that would do nothing, and both are provable in Node in about a
   * second. What is left here is a click handler.
   */
  const commit = async () => {
    const check = validateCommitment(commitDay, commitNote, today);
    if (!check.ok) {
      setCommitError(check.reason);
      return;
    }
    setCommitError(null);
    setBusy(true);
    try {
      await saveCommitment(
        dataProvider,
        contactId,
        commitDay,
        commitNote,
        today,
      );
      await invalidateLifecycle(queryClient);
      setCommitDay("");
      setCommitNote("");
      notify(`Committed to act by ${formatDay(commitDay)}.`, {
        type: "success",
      });
    } catch (e) {
      notify((e as Error).message ?? "Could not record the commitment", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const dropCommitment = async () => {
    setBusy(true);
    try {
      await clearCommitment(dataProvider, contactId);
      await invalidateLifecycle(queryClient);
      notify("Commitment cleared — the follow-up clock decides again.", {
        type: "success",
      });
    } catch (e) {
      notify((e as Error).message ?? "Could not clear the commitment", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const stageRow = findStage(allStages, health?.lifecycle_stage, health?.track);
  const pace = cadence(lifecycle, stageRow);
  const pauseNow = pauseState(health, today);
  /*
   * THE SAME FUNCTION THE PLAN SECTION USES, so the two halves of this page
   * cannot disagree about whether a commitment is still ahead. Its predicate
   * is the generator's, transcribed: a commitment holds while commitment_at
   * is STRICTLY in the future.
   */
  const held = suspension(health, today);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold">
          How often this contact should be chased
        </h2>
        <Separator className="mt-1.5" />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="lifecycle-track">Track</Label>
          <Select
            value={track ?? undefined}
            onValueChange={(v) => setDraftTrack(v as LifecycleTrack)}
            disabled={busy}
          >
            <SelectTrigger id="lifecycle-track" className="w-40">
              <SelectValue placeholder="No track" />
            </SelectTrigger>
            <SelectContent>
              {/* From the table, not from a list in here. Adding a track is
                  meant to be data. */}
              {tracksOf(allStages).map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="lifecycle-stage">Stage</Label>
          <Select
            // Cleared the moment the track being looked at differs from the
            // track on record: the stage shown must always belong to the
            // track above it, or the pair reads as one that exists.
            value={
              track === health?.track
                ? (health?.lifecycle_stage ?? undefined)
                : undefined
            }
            onValueChange={chooseStage}
            disabled={busy || !track}
          >
            <SelectTrigger id="lifecycle-stage" className="w-52">
              <SelectValue
                placeholder={track ? "Choose a stage" : "Pick a track first"}
              />
            </SelectTrigger>
            <SelectContent>
              {/*
                TRACK-SCOPED, and this is the whole guard. `new` and
                `contacted` exist in both tracks with different cadences, and
                contact_health joins on (stage, track) -- so offering a
                prospect's `demo` to a partner is how a contact lands on a
                join that matches nothing and falls to the 7-day floor.
              */}
              {stagesForTrack(allStages, track).map((s) => (
                <SelectItem key={`${s.track}:${s.stage}`} value={s.stage}>
                  {s.stage}
                  {s.is_terminal ? " (closes the record)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {draftTrack != null && draftTrack !== health?.track && (
        <p className="text-xs text-muted-foreground">
          Nothing is saved yet. Stage and track are one key, so choose a{" "}
          {draftTrack} stage to move this contact.
        </p>
      )}

      {/*
        WHAT THE PAIR ABOVE ACTUALLY DID.
        Changing track and stage produced a toast and no visible consequence,
        so the change read as inert -- but prospect/new to partner/active moves
        the cadence from 7 days to 30 and pushes the due date three weeks out.
        That is the whole point of the control and nothing on screen said it.
        Read from contact_health, so it is the value the nightly job will use,
        not one recomputed here.
      */}
      <ConsequenceLine health={health} />

      {/*
        A terminal stage has NO cadence -- lifecycle_stages.stale_after_days is
        NULL on won, lost, disqualified and dormant. contact_health still gives
        those rows an effective_stale_after of 7 because its coalesce has a
        floor, so printing the number would tell you to chase a closed deal
        every week.
      */}
      {state.kind === "terminal" ? (
        <p className="text-sm text-muted-foreground">
          Closed as{" "}
          <strong>
            {state.track} · {state.stage}
          </strong>
          . There is no cadence, because there is nothing left to be late for.
          Choosing another stage puts this contact back on the clock.
        </p>
      ) : health ? (
        <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">
            {describeCadence(
              pace.days,
              pace.source,
              stageRow
                ? { stage: stageRow.stage, track: stageRow.track }
                : null,
            )}
            {health.last_contacted_at ? (
              <>
                {" "}
                Last contacted <RelativeDate date={health.last_contacted_at} />.
              </>
            ) : (
              " Never contacted, so the clock runs from when the record was created."
            )}
          </p>

          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="lifecycle-cadence">Chase every (days)</Label>
              <Input
                id="lifecycle-cadence"
                type="number"
                min={1}
                className="w-32"
                /*
                  THE PLACEHOLDER IS THE INHERITED NUMBER, and the value is
                  only the override. An empty field is not missing data -- it
                  is this contact saying "whatever my stage says", and it has
                  to be typeable back to, or setting 3 days during a
                  negotiation is a one-way door out of the stage's 30.
                */
                placeholder={String(pace.inherited)}
                value={
                  cadenceDraft ?? String(lifecycle?.stale_after_days ?? "")
                }
                onChange={(e) => {
                  setCadenceDraft(e.target.value);
                  setCadenceError(null);
                }}
                disabled={busy}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                applyCadence(
                  cadenceDraft ?? String(lifecycle?.stale_after_days ?? ""),
                )
              }
              disabled={busy}
            >
              Apply
            </Button>
          </div>

          {cadenceError ? (
            <p className="text-xs text-destructive">{cadenceError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {pace.source === "record"
                ? `Overriding the inherited ${pace.inherited} days. Clear the field and apply to go back to it.`
                : `Empty means inherit — ${pace.inherited} days.`}
            </p>
          )}
        </div>
      ) : null}

      {/*
        A PAUSE AND A COMMITMENT ARE NOT THE SAME THING and never share a
        control. A pause is US deciding to stop -- a dated snooze with a
        reason, permission to stop looking. A commitment is THEM asking us to
        wait, and it is a promise to act by a time.

        Both push contact_health.due_at out, so it is tempting to call them
        one control with two labels. The views say otherwise, read from their
        live definitions 2026-09-04:

          is_paused               := paused_until is not null
                                     and paused_until >= current_date
          contacts_no_next_action := ... and not is_paused

        commitment_at is nowhere in is_paused. So a paused contact DROPS OUT
        of the no-next-action queue and a committed one STAYS IN IT -- still
        on the board, simply not reported late before the date they asked for.
        Merging the two would let a promise be entered as an excuse, and that
        substitution is how somebody goes quiet for nine months.

        The two blocks below say that difference in words, because a person
        choosing between them cannot read a view definition.
      */}
      <div className="space-y-1.5">
        {pauseNow.paused ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>
              Quiet until <strong>{formatDay(pauseNow.until!)}</strong>
              {pauseNow.reason ? ` — ${pauseNow.reason}` : ""}. It comes back on
              its own the day after.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={resume}
              disabled={busy}
            >
              Resume now
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="lifecycle-pause-until">Pause until</Label>
              <Input
                id="lifecycle-pause-until"
                type="date"
                className="w-44"
                value={pauseUntil}
                onChange={(e) => {
                  setPauseUntil(e.target.value);
                  setPauseError(null);
                }}
              />
            </div>
            <div className="space-y-1.5 flex-1 min-w-48">
              <Label htmlFor="lifecycle-pause-reason">Why</Label>
              <Input
                id="lifecycle-pause-reason"
                placeholder="Waiting on their permit…"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
              />
            </div>
            <Button variant="outline" size="sm" onClick={pause} disabled={busy}>
              Pause
            </Button>
          </div>
        )}

        {pauseError ? (
          <p className="text-xs text-destructive">{pauseError}</p>
        ) : pauseNow.paused ? null : (
          <p className="text-xs text-muted-foreground">
            A pause is <strong>us</strong> deciding to stop — it takes this
            contact off the no-next-action queue until the date, so nobody is
            expected to be looking at them. It is always dated; an open-ended
            one is how people go quiet for nine months.
            {pauseNow.until
              ? ` The last pause ended on ${formatDay(pauseNow.until)}.`
              : ""}
          </p>
        )}
      </div>

      {/*
        THE CONTROL atomic#7 IS ABOUT. Both columns were displayed here and on
        the follow-ups queue, both are read by contact_health and by
        advance_enrollments(), and nothing in the app could set either --
        making one took hand-written SQL. That is the definition of a dark
        feature: a mechanism the schema supports and the person it was built
        for cannot reach.

        The open question the issue left -- whether a commitment should be
        derived from a task rather than stored -- was settled in favor of the
        columns the three readers already read. See commitment.ts.
      */}
      <div className="space-y-1.5">
        {held.commitmentAt ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>
              <strong>They asked us to wait</strong> until{" "}
              <strong>
                <RelativeDate date={held.commitmentAt} />
              </strong>
              {held.commitmentNote ? ` — ${held.commitmentNote}` : ""}. Nothing
              is chased before then, and this contact stays on the board.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={dropCommitment}
              disabled={busy}
            >
              <Handshake className="size-4" /> Clear it
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="lifecycle-commitment-day">
                They asked us to wait until
              </Label>
              <Input
                id="lifecycle-commitment-day"
                type="date"
                className="w-44"
                value={commitDay}
                onChange={(e) => {
                  setCommitDay(e.target.value);
                  setCommitError(null);
                }}
              />
            </div>
            <div className="space-y-1.5 flex-1 min-w-48">
              <Label htmlFor="lifecycle-commitment-note">What they said</Label>
              <Input
                id="lifecycle-commitment-note"
                placeholder="Call me Thursday, after the board meets…"
                value={commitNote}
                onChange={(e) => setCommitNote(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={commit}
              disabled={busy}
            >
              Record commitment
            </Button>
          </div>
        )}

        {commitError ? (
          <p className="text-xs text-destructive">{commitError}</p>
        ) : held.commitmentAt ? null : (
          <p className="text-xs text-muted-foreground">
            A commitment is <strong>them</strong> asking us to wait. It moves
            the due date out without pausing, so this contact stays on the
            no-next-action queue and stays chaseable — they are simply not
            reported late before the day you actually promised.
            {health?.commitment_at
              ? ` The last one was ${formatDay(dateInputValue(health.commitment_at))}, and it has passed.`
              : ""}
          </p>
        )}
      </div>
    </section>
  );
};

/* ── the landing page, with nobody chosen ──────────────────────────────── */

/**
 * `/contacts/lifecycle` — which is a question, not an index.
 *
 * WHY IT SHOWS THE ENROLLMENT AND THE STAGE RATHER THAN JUST NAMES. A picker
 * that lists seventy contacts alphabetically answers "open a person"; this
 * one has to answer "who am I working right now", which is the question
 * somebody actually arrives with. So every row carries the two facts that
 * decide it: where they are, and whether a cadence is running on them.
 *
 * SORTED BY WHEN THEY ARE DUE, most overdue first. Alphabetical order is a
 * property of the names; due order is a property of the work.
 *
 * FILTERED IN THE BROWSER, DELIBERATELY. There are seventy contacts. A
 * server-side search would mean a PostgREST text filter, a debounce and a
 * loading state to answer a question one array `.filter()` answers over rows
 * already held — and every count on the page would then be a count of the
 * page rather than of the database. Revisit it when the number of contacts
 * makes one read of them unreasonable, not before.
 */
export const LifecycleIndex = () => {
  /*
   * useStore, not useState. This is a FILTER, and a filter in useState resets
   * every time somebody opens a contact and comes back -- so a queue-driven
   * screen forgets what you told it at the exact moment you are working
   * through it. See atomic#13.
   */
  const [query, setQuery] = useStore<string>("lifecycle.picker.q", "");

  const {
    data: healthRows,
    isPending,
    error,
  } = useGetList<ContactHealth>("contact_health", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "due_at", order: "ASC" },
  });

  const { data: enrollmentRows } = useGetList<Enrollment>("enrollments", {
    filter: { status: "active" },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "created_at", order: "DESC" },
  });

  /*
   * Every PENDING step in the database, rather than one query per
   * enrollment. There are five rows in this table today and one active
   * enrollment; a query per contact would be seventy requests to answer a
   * question one request answers. If that stops being true, the honest fix is
   * a view, not seventy requests.
   */
  const { data: stepRows } = useGetList<EnrollmentStep>("enrollment_steps", {
    filter: { status: "pending" },
    pagination: { page: 1, perPage: ALL_STEPS },
    sort: { field: "due_at", order: "ASC" },
  });

  const { data: sequenceRows } = useGetList<Sequence>("sequences", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const needle = query.trim().toLowerCase();
  const rows = (healthRows ?? []).filter((h) =>
    needle
      ? `${h.first_name ?? ""} ${h.last_name ?? ""}`
          .toLowerCase()
          .includes(needle)
      : true,
  );

  const enrollmentOf = new Map<number, Enrollment>();
  for (const e of enrollmentRows ?? []) enrollmentOf.set(e.contact_id, e);
  const nextStepOf = new Map<number, EnrollmentStep>();
  for (const s of stepRows ?? [])
    if (!nextStepOf.has(s.enrollment_id)) nextStepOf.set(s.enrollment_id, s);
  const sequenceName = (id: number | null) =>
    id == null
      ? "a hand-built plan"
      : ((sequenceRows ?? []).find((s) => s.id === id)?.name ??
        `sequence #${id}`);

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Lifecycle</h1>
        <p className="text-sm text-muted-foreground">
          Who is being worked, where they are, and what is running on them. Most
          overdue first. Pick somebody to open their plan.
        </p>
      </div>

      <div className="space-y-1.5 max-w-sm">
        <Label htmlFor="lifecycle-search">Find a contact</Label>
        <Input
          id="lifecycle-search"
          placeholder="Name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error ? (
        <p className="text-sm text-destructive">
          Could not read the contacts, so this is not a record of there being
          nobody to work — it is a record of not knowing.
        </p>
      ) : isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {needle
            ? `Nobody matches “${query.trim()}”.`
            : "No contacts yet. Nothing to work."}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {rows.map((h) => {
            const enrollment = enrollmentOf.get(Number(h.id));
            const next = enrollment ? nextStepOf.get(enrollment.id) : undefined;
            return (
              <li key={h.id}>
                <Link
                  to={lifecyclePath(h.id)}
                  className="flex flex-wrap items-baseline justify-between gap-2 p-3 hover:bg-accent"
                >
                  <span className="text-sm font-medium">
                    {`${h.first_name ?? ""} ${h.last_name ?? ""}`.trim() ||
                      `contact #${h.id}`}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {h.track && h.lifecycle_stage
                        ? `${h.track} · ${h.lifecycle_stage}`
                        : "untriaged"}
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {enrollment ? (
                      <>
                        In{" "}
                        <strong>{sequenceName(enrollment.sequence_id)}</strong>
                        {next ? (
                          <>
                            {" — next step "}
                            <RelativeDate date={next.due_at} />
                          </>
                        ) : (
                          " — nothing pending"
                        )}
                      </>
                    ) : (
                      "Not enrolled"
                    )}
                    {h.is_terminal
                      ? " · closed"
                      : h.is_paused
                        ? " · paused"
                        : h.is_stale
                          ? " · overdue"
                          : ""}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-xs text-muted-foreground">
        Sequences themselves are written on the{" "}
        <Link
          to={MARKETING_PATHS.sequences}
          className="underline underline-offset-2"
        >
          Sequences
        </Link>{" "}
        screen. This one is about people.
      </p>
    </div>
  );
};
